import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Block, Movement, User, Notification } from "@/types";

interface Settings {
  darkMode: boolean;
  notifications: boolean;
  rtgAlerts: boolean;
  movementAlerts: boolean;
  animations: boolean;
  sidebarCollapsed: boolean;
}

interface AppState {
  user: User | null;
  blocks: Block[];
  movements: Movement[];
  notifications: Notification[];
  settings: Settings;
  pendingCancellation: {
    movementId: string;
    timeout: ReturnType<typeof setTimeout>;
  } | null;
  setUser: (user: User | null) => void;
  setBlocks: (blocks: Block[]) => void;
  addBlock: (block: Block) => void;
  removeBlock: (blockId: string) => void;
  addSubBlock: (blockId: string, subBlockName: string) => void;
  removeSubBlock: (blockId: string, subBlockId: string) => void;
  updateSubBlockOrder: (
    blockId: string,
    subBlockId: string,
    priority: number,
  ) => void;
  addMovement: (movement: Movement) => void;
  updateRTGPosition: (
    rtgId: string,
    newSubBlockId: string,
    newBlockId: string,
  ) => void;
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markNotificationRead: (notificationId: string) => void;
  updateSettings: (settings: Partial<Settings>) => void;
  setPendingCancellation: (
    data: { movementId: string; timeout: ReturnType<typeof setTimeout> } | null,
  ) => void;
}

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      user: null,
      blocks: [],
      movements: [],
      notifications: [],
      pendingCancellation: null,
      settings: {
        darkMode: false,
        notifications: true,
        rtgAlerts: true,
        movementAlerts: true,
        animations: true,
        sidebarCollapsed: false,
      },
      setUser: (user) => set({ user }),
      setBlocks: (blocks) => set({ blocks }),
      addBlock: (block) =>
        set((state) => ({
          blocks: [...state.blocks, block],
        })),
      removeBlock: (blockId) =>
        set((state) => ({
          blocks: state.blocks.filter((b) => b.id !== blockId),
        })),
      addSubBlock: (blockId, subBlockName) =>
        set((state) => ({
          blocks: state.blocks.map((block) =>
            block.id === blockId
              ? {
                  ...block,
                  subBlocks: [
                    ...block.subBlocks,
                    {
                      id: `${blockId}-${Date.now()}`,
                      blockId,
                      name: subBlockName,
                      rtgs: [],
                      priority: block.subBlocks.length + 1,
                    },
                  ],
                }
              : block,
          ),
        })),
      removeSubBlock: (blockId, subBlockId) =>
        set((state) => ({
          blocks: state.blocks.map((block) =>
            block.id === blockId
              ? {
                  ...block,
                  subBlocks: block.subBlocks.filter(
                    (sb) => sb.id !== subBlockId,
                  ),
                }
              : block,
          ),
        })),
      updateSubBlockOrder: (blockId, subBlockId, priority) =>
        set((state) => ({
          blocks: state.blocks.map((block) =>
            block.id === blockId
              ? {
                  ...block,
                  subBlocks: block.subBlocks.map((sb) =>
                    sb.id === subBlockId ? { ...sb, priority } : sb,
                  ),
                }
              : block,
          ),
        })),
      addMovement: (movement) =>
        set((state) => ({
          movements: [movement, ...state.movements],
        })),
      updateRTGPosition: (rtgId, newSubBlockId, newBlockId) =>
        set((state) => {
          const newBlocks = state.blocks.map((block) => ({
            ...block,
            subBlocks: block.subBlocks.map((subBlock) => ({
              ...subBlock,
              rtgs: subBlock.rtgs.filter((rtg) => rtg.id !== rtgId),
            })),
          }));

          const targetBlock = newBlocks.find((b) => b.id === newBlockId);
          if (targetBlock) {
            const targetSubBlock = targetBlock.subBlocks.find(
              (sb) => sb.id === newSubBlockId,
            );
            if (targetSubBlock) {
              const rtg = state.blocks
                .flatMap((b) => b.subBlocks)
                .flatMap((sb) => sb.rtgs)
                .find((r) => r.id === rtgId);

              if (rtg) {
                targetSubBlock.rtgs.push({
                  ...rtg,
                  blockId: newBlockId,
                  subBlockId: newSubBlockId,
                });
              }
            }
          }

          return { blocks: newBlocks };
        }),
      setNotifications: (notifications) => set({ notifications }),
      addNotification: (notification) =>
        set((state) => ({
          notifications: [notification, ...state.notifications],
        })),
      markNotificationRead: (notificationId) =>
        set((state) => ({
          notifications: state.notifications.map((notif) =>
            notif.id === notificationId ? { ...notif, read: true } : notif,
          ),
        })),
      updateSettings: (newSettings) =>
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        })),
      setPendingCancellation: (data) => set({ pendingCancellation: data }),
    }),
    {
      name: "ct-storage",
      partialize: (state) => ({ settings: state.settings }),
    },
  ),
);
